import { Table } from "react-bootstrap";
import ReactSwitch from "react-switch";
import type { IAttendanceManagementConfiguration } from "../../../../models/AttendanceManagement";

interface IManagementCodeCatalogueProps {
    configuration: IAttendanceManagementConfiguration;
    isEditing: boolean;
    onChange: (configuration: IAttendanceManagementConfiguration) => void;
}

function updateManagementCodeCollection(
    values: string[],
    code: string,
    checked: boolean,
): string[] {
    return checked
        ? Array.from(new Set([...values, code]))
        : values.filter((value) => value !== code);
}

export default function CodeCatalogueV2(props: IManagementCodeCatalogueProps) {
    const updateCode = (
        code: string,
        field: "enabled" | "protected" | "overwritable",
        checked: boolean,
    ) => {
        if (field === "enabled") {
            props.onChange({
                ...props.configuration,
                codeCatalogue: props.configuration.codeCatalogue.map((entry) => (
                    entry.code === code ? { ...entry, enabled: checked } : entry
                )),
            });
            return;
        }

        props.onChange({
            ...props.configuration,
            protectedMarks: field === "protected"
                ? updateManagementCodeCollection(props.configuration.protectedMarks, code, checked)
                : props.configuration.protectedMarks,
            overwritableMarks: field === "overwritable"
                ? updateManagementCodeCollection(props.configuration.overwritableMarks, code, checked)
                : props.configuration.overwritableMarks,
        });
    };

    const codeCatalogue = [...props.configuration.codeCatalogue]
        .sort((left, right) => left.displayOrder - right.displayOrder || left.code.localeCompare(right.code));

    return (
        <div className="attendance-code-catalogue">
            <section className="attendance-code-catalogue-intro">
                <div>
                    <h2 id="attendance-section-heading">Code Catalogue</h2>
                    <p>Configure the server-provided attendance codes and their independent mark policies.</p>
                </div>
            </section>
            <div
                aria-label="Attendance code catalogue table"
                className="table-responsive"
                role="region"
                tabIndex={0}
            >
                <Table className="attendance-data-table attendance-code-catalogue-table mb-0">
                    <caption className="visually-hidden">Server attendance code catalogue and mark policies.</caption>
                    <thead>
                        <tr>
                            <th scope="col">No.</th>
                            <th scope="col">Code</th>
                            <th scope="col">Description</th>
                            <th scope="col">Category</th>
                            <th scope="col">Enabled</th>
                            <th scope="col">Protected</th>
                            <th scope="col">Allow Overwrite</th>
                        </tr>
                    </thead>
                    <tbody>
                        {codeCatalogue.map((entry, index) => {
                            const isProtected = props.configuration.protectedMarks.includes(entry.code);
                            const isOverwritable = props.configuration.overwritableMarks.includes(entry.code);

                            return (
                                <tr key={entry.code}>
                                    <td>{index + 1}</td>
                                    <td><code className="attendance-code-token">{entry.code}</code></td>
                                    <td>{entry.description}</td>
                                    <td><span className="attendance-code-category">{entry.category}</span></td>
                                    <td>
                                        <span data-testid={`attendance-code-${entry.code}-enabled`}>
                                            <ReactSwitch
                                                aria-label={`Enable ${entry.code}`}
                                                checked={entry.enabled}
                                                checkedIcon={false}
                                                disabled={!props.isEditing}
                                                handleDiameter={18}
                                                height={22}
                                                offColor="#98A2B3"
                                                onChange={(checked) => updateCode(entry.code, "enabled", checked)}
                                                onColor="#1B56A5"
                                                uncheckedIcon={false}
                                                width={42}
                                            />
                                        </span>
                                    </td>
                                    <td>
                                        <span data-testid={`attendance-code-${entry.code}-protected`}>
                                            <ReactSwitch
                                                aria-label={`Protect ${entry.code}`}
                                                checked={isProtected}
                                                checkedIcon={false}
                                                disabled={!props.isEditing}
                                                handleDiameter={18}
                                                height={22}
                                                offColor="#98A2B3"
                                                onChange={(checked) => updateCode(entry.code, "protected", checked)}
                                                onColor="#1B56A5"
                                                uncheckedIcon={false}
                                                width={42}
                                            />
                                        </span>
                                    </td>
                                    <td>
                                        <span data-testid={`attendance-code-${entry.code}-overwritable`}>
                                            <ReactSwitch
                                                aria-label={`Allow overwrite for ${entry.code}`}
                                                checked={isOverwritable}
                                                checkedIcon={false}
                                                disabled={!props.isEditing}
                                                handleDiameter={18}
                                                height={22}
                                                offColor="#98A2B3"
                                                onChange={(checked) => updateCode(entry.code, "overwritable", checked)}
                                                onColor="#1B56A5"
                                                uncheckedIcon={false}
                                                width={42}
                                            />
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                        {codeCatalogue.length === 0 && (
                            <tr>
                                <td className="attendance-monitor-empty" colSpan={7}>No attendance codes are available.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}